import React, { createContext, useContext, useState, useEffect } from 'react';
import { UserSubscription, SubscriptionPlan } from '../types/subscription';
import { getSubscription, createCheckoutSession } from '../services/subscriptionService';
import { useAuth } from './AuthContext';
import { getPlanByInterval } from '../constants/subscriptionPlans';
import { Capacitor } from '@capacitor/core';
import { appleOfferCode } from '../services/appleOfferCode';
import { iapService } from '../services/iapService';

interface SubscriptionContextType {
  subscription: UserSubscription | null;
  isLoading: boolean;
  plan: SubscriptionPlan;
  isActive: boolean;
  isPremium: boolean;
  isPro: boolean;
  isFree: boolean;
  trialJustExpired: boolean;
  canAccessFeature: (feature: string) => boolean;
  upgradeToPlan: (plan: SubscriptionPlan, billingInterval?: 'monthly' | 'annual', promotionCode?: string) => Promise<void>;
  manageSubscription: () => Promise<void>;
  refreshSubscription: () => Promise<void>;
  dismissTrialExpired: () => void;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [subscription, setSubscription] = useState<UserSubscription | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [trialJustExpired, setTrialJustExpired] = useState(false);
  const { user } = useAuth();

  const syncAppleSubscription = async (firestoreSub: UserSubscription | null) => {
    if (!user?.uid || !appleOfferCode.isSupported()) return firestoreSub;
    try {
      const apple = await appleOfferCode.getActiveSubscription();
      if (!apple.hasActive || !apple.productId || !apple.transactionId) return firestoreSub;

      const productMatches = firestoreSub?.appleProductId === apple.productId;
      const transactionMatches = firestoreSub?.appleTransactionId === apple.transactionId;
      if (productMatches && transactionMatches && firestoreSub?.status === 'active') {
        return firestoreSub;
      }

      await iapService.validateAndActivate(user.uid, apple.transactionId, apple.productId);
      return await getSubscription(user.uid);
    } catch {
      return firestoreSub;
    }
  };

  const refreshSubscription = async () => {
    if (!user?.uid) {
      setSubscription(null);
      setIsLoading(false);
      return;
    }

    try {
      let sub = await getSubscription(user.uid);
      sub = await syncAppleSubscription(sub);

      if (sub && sub.status === 'trialing' && sub.trialEnd) {
        const now = new Date();
        const trialEnd = new Date(sub.trialEnd);

        if (now > trialEnd) {
          sub.status = 'active';
          sub.plan = 'free';

          const { doc, updateDoc } = await import('firebase/firestore');
          const { db } = await import('../config/firebase');
          const subscriptionRef = doc(db, 'users', user.uid, 'subscription', 'current');
          await updateDoc(subscriptionRef, {
            plan: 'free',
            status: 'active',
            updatedAt: new Date(),
          });

          setTrialJustExpired(true);
        }
      }

      setSubscription(sub);
    } catch (error) {
      setSubscription(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    refreshSubscription();
  }, [user?.uid]);

  const isTrialing = subscription?.status === 'trialing';

  // Use the plan from the subscription data (set by webhook), not hardcoded
  const plan = subscription?.plan || 'free';

  // Helper to check expiration
  const checkIsExpired = () => {
    if (!subscription?.currentPeriodEnd) return false;
    // For internal/manual providers, strictly curb access after date
    if (subscription.provider === 'internal' || subscription.provider === 'apple') {
      return new Date() > new Date(subscription.currentPeriodEnd);
    }
    // For Stripe, we usually rely on status, but can double check date if status is ambiguous
    return false;
  };

  const isExpired = checkIsExpired();

  const isActive = !isExpired && (
    (subscription?.status === 'active' && (subscription.plan === 'premium' || subscription.plan === 'pro')) ||
    (isTrialing && (subscription.plan === 'premium' || subscription.plan === 'pro'))
  );

  const isPremium = !isExpired && ((plan === 'premium' || (subscription?.plan as string) === 'basic') && (subscription?.status === 'active' || isTrialing));
  const isPro = !isExpired && (plan === 'pro' && (subscription?.status === 'active' || isTrialing));
  const isFree = !isTrialing && (!isActive || !subscription || subscription.plan === 'free');

  const canAccessFeature = (feature: string): boolean => {
    if (isPro) return true;

    const proFeatures = [
      'ai-assistant',
      'menu-suggestions',
      'dish-combinations',
      'etiquette-tips',
      'preference-analysis',
      'offline-mode',
      'priority-support',
      'advanced-reports',
      'video-recipes',
      'cost-reports'
    ];

    const basicFeatures = [
      'google-calendar',
      'backup',
      'export-data',
      'basic-reports',
      'portion-calculator',
      'usage-history',
      'invite-tracking',
      'advanced-filters',
      'inventory-sharing',
      'recipe-sharing'
    ];

    if (proFeatures.includes(feature)) {
      return isPro;
    }

    if (basicFeatures.includes(feature)) {
      return isPremium || isPro || isTrialing;
    }

    return true;
  };

  const upgradeToPlan = async (targetPlan: SubscriptionPlan, billingInterval: 'monthly' | 'annual' = 'monthly', promotionCode?: string) => {
    if (!user?.uid || !user?.email) {
      throw new Error('Usuário não autenticado');
    }

    if (targetPlan === 'free') {
      throw new Error('Não é possível fazer downgrade para o plano free');
    }

    // Buscar plano com o intervalo correto
    const planDetails = getPlanByInterval(targetPlan, billingInterval);

    if (!planDetails.stripePriceId) {
      throw new Error('ID do preço do Stripe não encontrado. Configure os Price IDs no arquivo .env');
    }

    // Verificar se o priceId é válido (deve começar com "price_" e ter mais caracteres)
    if (!planDetails.stripePriceId.startsWith('price_') || planDetails.stripePriceId.length < 20) {
      throw new Error(`Price ID inválido: ${planDetails.stripePriceId}. Você precisa criar os produtos no Stripe Dashboard e configurar os Price IDs reais no arquivo .env`);
    }

    try {
      const { url } = await createCheckoutSession(
        user.uid,
        planDetails.stripePriceId,
        targetPlan,
        user.email,
        promotionCode
      );

      // Redirecionar para o checkout do Stripe
      window.location.href = url;
    } catch (error) {
      console.error('Erro ao criar checkout:', error);
      throw error;
    }
  };

  const manageSubscription = async () => {
    if (Capacitor.getPlatform() === 'ios') {
      throw new Error('Gerencie sua assinatura nas configurações do iPhone (Ajustes > Seu Nome > Assinaturas)');
    }

    if (!user?.uid || !subscription?.stripeCustomerId) {
      throw new Error('Você ainda não possui uma assinatura ativa para gerenciar.');
    }

    try {
      const { stripeApi } = await import('../services/stripeApiService');
      const result = await stripeApi.createPortalSession({
        customer: subscription.stripeCustomerId,
        return_url: window.location.href,
      });

      if (result.success && result.data?.session?.url) {
        window.location.href = result.data.session.url;
      } else {
        throw new Error(result.error || 'Erro ao abrir portal de assinatura');
      }
    } catch (error) {
      throw error;
    }
  };

  const dismissTrialExpired = () => {
    setTrialJustExpired(false);
  };

  const value = {
    subscription,
    isLoading,
    plan,
    isActive,
    isPremium,
    isPro,
    isFree,
    trialJustExpired,
    canAccessFeature,
    upgradeToPlan,
    manageSubscription,
    refreshSubscription,
    dismissTrialExpired,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};
